import React from "react";
import { Tag, Space, Button, Tooltip, Popover, Card, Table } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

// Helper function to get status tag
const getStatusTag = (status) => {
  switch (status?.toLowerCase()) {
    case "paid":
      return <Tag color="success">Paid</Tag>;
    case "overdue":
      return <Tag color="error">Overdue</Tag>;
    case "cancelled":
      return <Tag color="default">Cancelled</Tag>;
    case "draft":
      return <Tag color="warning">Draft</Tag>;
    case "sent":
      return <Tag color="processing">Sent</Tag>;
    default:
      return <Tag color="default">{status || "Unknown"}</Tag>;
  }
};

// Function to create invoice columns
const createInvoiceColumns = ({
  filterValues = {},
  handleEditInvoice,
  handleDeleteInvoice,
  handleExportPDF,
  hasPermission,
}) => [
  {
    title: "Number",
    dataIndex: "number",
    key: "number",
    sorter: true, // Enable server-side sorting
  },

  {
    title: "Subtotal",
    dataIndex: "subtotal",
    key: "subtotal",
    render: (subtotal, record) =>
      `${record.currency || "$"} ${
        subtotal !== undefined && subtotal !== null
          ? parseFloat(subtotal).toFixed(2)
          : "0.00"
      }`,
    sorter: true, // Enable server-side sorting
  },
  {
    title: "Discount",
    dataIndex: "discount_amount",
    key: "discount_amount",
    render: (discount, record) =>
      `${record.currency || "$"} ${
        discount !== undefined && discount !== null
          ? parseFloat(discount).toFixed(2)
          : "0.00"
      }`,
    sorter: true, // Enable server-side sorting
  },

  {
    title: "Tax Rate",
    dataIndex: "tax_rate",
    key: "tax_rate",
    render: (taxRate) =>
      taxRate !== undefined && taxRate !== null
        ? `${parseFloat(taxRate).toFixed(2)}%`
        : "0.00%",
    sorter: true, // Enable server-side sorting
  },
  {
    title: "Total",
    dataIndex: "total",
    key: "total",
    render: (total, record) =>
      `${record.currency || "$"} ${
        total !== undefined && total !== null
          ? parseFloat(total).toFixed(2)
          : "0.00"
      }`,
    sorter: true, // Enable server-side sorting
  },
  {
    title: "From",
    key: "from",
    dataIndex: "from",
    render: (_, record) => {
      return record.invoice_type === "bill"
        ? record.partner
        : record.originalData.brand?.brand_name || "Unknown";
    },
    sorter: true, // Enable server-side sorting
    filters:
      filterValues.from?.map((value) => {
        // Parse the value format (partner:Name or brand:Name)
        const [, name] = value.split(":"); // Ignore type, only use name
        return { text: name, value };
      }) || [],
    filterMultiple: true, // Allow multiple selections
    filterSearch: true, // Enable search in filter dropdown
  },
  {
    title: "To",
    key: "to",
    dataIndex: "to",
    render: (_, record) => {
      return record.invoice_type === "invoice"
        ? record.partner
        : record.originalData.brand?.brand_name || "Unknown";
    },
    sorter: true, // Enable server-side sorting
    filters:
      filterValues.to?.map((value) => {
        // Parse the value format (partner:Name or brand:Name)
        const [, name] = value.split(":"); // Ignore type, only use name
        return { text: name, value };
      }) || [],
    filterMultiple: true, // Allow multiple selections
    filterSearch: true, // Enable search in filter dropdown
  },
  {
    title: "Type",
    dataIndex: "invoice_type",
    key: "invoice_type",
    render: (type) =>
      type === "invoice" ? (
        <Tag color="blue">Invoice</Tag>
      ) : (
        <Tag color="purple">Bill</Tag>
      ),
    filters: [
      { text: "Invoice", value: "invoice" },
      { text: "Bill", value: "bill" },
    ],
    onFilter: (value, record) => record.invoice_type === value,
    filterMultiple: true, // Allow multiple selections
    filterSearch: true, // Enable search in filter dropdown
  },
  {
    title: "Items Count",
    dataIndex: "items_count",
    key: "items_count",
    render: (count) => count || 0,
    sorter: true, // Enable server-side sorting
  },
  {
    title: "Issue Date",
    dataIndex: "issueDate",
    key: "issueDate",
    sorter: true, // Enable server-side sorting
  },

  // Due Date column removed as it's not in the optimized API response

  {
    title: "Paid",
    dataIndex: "paid",
    key: "paid",
    render: (paid, record) => {
      const amount = record.paid_amount ?? 0;
      const currency = record.currency === "USD" ? "$" : record.currency;

      return (
        <span>
          {currency} {parseFloat(amount).toFixed(2)}
        </span>
      );
    },
    sorter: true, // Enable server-side sorting
    filters: [
      { text: "Fully Paid", value: "fully_paid" },
      { text: "Partially Paid", value: "partially_paid" },
      { text: "Unpaid", value: "unpaid" },
    ],
    filterMultiple: true, // Allow multiple selections
    filterSearch: true, // Enable search in filter dropdown
  },
  {
    title: "Paid Percentage",
    dataIndex: "percentPaid",
    key: "percentPaid",
    render: (percentPaid) =>
      percentPaid !== undefined && percentPaid !== null
        ? `${percentPaid.toFixed(2)}%`
        : "0.00%",
    sorter: true, // Optional: enable server-side sorting if needed
  },
  {
    title: "Payments",
    key: "payments",
    render: (_, record) => {
      // Only show the payments button if there are allocations
      if (!record.allocations || record.allocations.length === 0) {
        return <span>No payments</span>;
      }

      // Create a table of payment allocations
      const content = (
        <Card
          title={`Payments for Invoice #${record.number}`}
          style={{ width: 300, maxHeight: 400, overflow: "auto" }}
        >
          <Table
            dataSource={record.allocations}
            columns={[
              {
                title: "Transaction Description",
                dataIndex: "transaction_description",
                key: "transaction_description",
                render: (desc, allocation) => {
                  // Extract transaction ID - handle both object and primitive
                  const transactionId = typeof allocation.transaction === 'object' 
                    ? allocation.transaction?.id 
                    : allocation.transaction;
                  
                  // If there's a transaction ID, make it clickable
                  if (transactionId && allocation.transaction_description) {
                    return (
                      <a
                        href={`#/accounting/transactions-allocations?transaction=${transactionId}`}
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = `/accounting/transactions-allocations?transaction=${transactionId}`;
                        }}
                      >
                        {allocation.transaction_description}
                      </a>
                    );
                  }
                  return desc || "No description";
                },
              },
              {
                title: "Payment Amount",
                dataIndex: "amount",
                key: "amount",
                render: (amount) =>
                  `${record.currency || "$"} ${
                    amount !== undefined && amount !== null
                      ? parseFloat(amount).toFixed(2)
                      : "0.00"
                  }`,
              },
              {
                title: "Payment Date",
                dataIndex: "transaction_date",
                key: "transaction_date",
                render: (date, allocation) =>
                  allocation.transaction_date
                    ? new Date(allocation.transaction_date).toLocaleDateString()
                    : allocation.allocation_date
                    ? new Date(allocation.allocation_date).toLocaleDateString()
                    : "N/A",
              },
            ]}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </Card>
      );

      return (
        <Popover
          content={content}
          title={`${record.allocations.length} Payment${
            record.allocations.length > 1 ? "s" : ""
          }`}
          trigger="click"
          placement="right"
        >
          <Button type="primary" size="small" icon={<DollarOutlined />}>
            {record.allocations.length} Payment
            {record.allocations.length > 1 ? "s" : ""}
          </Button>
        </Popover>
      );
    },
  },
  {
    title: "Payment Status",
    key: "payment_status",
    render: (_, record) => {
      if (record.allocationStatus) {
        return (
          <Tag color={record.allocationStatus.color}>
            {record.allocationStatus.label}
          </Tag>
        );
      }
      return <Tag color="default">Unknown</Tag>;
    },
    filters: [
      { text: "Fully Paid", value: "fully_paid" },
      { text: "Partially Paid", value: "partially_paid" },
      { text: "Unpaid", value: "unpaid" },
    ],
    onFilter: (value, record) => record.allocationStatus?.type === value,
  },

  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => getStatusTag(status),
    sorter: true, // Enable server-side sorting
    filters: filterValues.status?.map((value) => ({
      text: value,
      value,
    })) || [
      { text: "Paid", value: "paid" },
      { text: "Overdue", value: "overdue" },
      { text: "Cancelled", value: "cancelled" },
    ],
    onFilter: (value, record) => record.status === value,
    filterMultiple: true, // Allow multiple selections
    filterSearch: true, // Enable search in filter dropdown
  },
  {
    title: "Last Modified By",
    dataIndex: "last_modified_by",
    key: "last_modified_by",
    render: (username) => username || "Unknown",
    sorter: true, // Enable server-side sorting
  },
  {
    title: "Actions",
    key: "actions",
    render: (_, record) => (
      <Space size="small">
        <Tooltip title="Export PDF">
          <Button
            type="text"
            icon={<FilePdfOutlined />}
            onClick={() => handleExportPDF && handleExportPDF(record.id)}
            style={{ color: "#ff4d4f" }}
          />
        </Tooltip>
        {hasPermission && hasPermission("accounting/invoices/edit") && (
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditInvoice(record.id)}
            />
          </Tooltip>
        )}
        {hasPermission && hasPermission("accounting/invoices/delete") && (
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteInvoice(record.id)}
            />
          </Tooltip>
        )}
      </Space>
    ),
  },
];

export default createInvoiceColumns;
